import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  SpaceBetween,
  Button,
  Alert,
  Box,
  Flashbar,
} from '@cloudscape-design/components';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Question } from 'src/types/helios/Question';
import { useUpdateQuestionTranslationMutation } from 'src/data/api/helios-service-ts/question/useUpdateQuestionTranslationMutation';
import {
  buildTranslationRequest,
  getLanguageLabel,
  hasExistingTranslation,
  hasImagesChanged,
} from 'src/helper/translationHelper';
import { useNavigate } from 'react-router-dom';
import { openQuestionDetailsPage, getQuestionContent } from 'src/helper/questionHelper';
import { useQuestion } from 'src/store/question/useQuestion';
import useUserProfile from 'src/hooks/useUserProfile';
import { useTranslationFormData } from 'src/hooks/useTranslationFormData';
import {
  translationFormSchema,
  TranslationFormData,
} from 'src/validations/translationFormSchema';
import { ImageModificationErrorModal } from './ImageModificationErrorModal';
import { TranslationConfirmationModal } from './TranslationConfirmationModal';
import { TranslationQuestionContent } from './TranslationQuestionContent';
import { TranslationTaskDetails } from './TranslationTaskDetails';
import { TranslationSubQuestions } from './TranslationSubQuestions';
import { QuestionReadonlyDetails } from '../QuestionReadonlyDetails';
import { useImageChangeDetection } from './useImageChangeDetection';
import TranslationStalenessAlert from './TranslationStalenessAlert';
import { useUserDetails } from 'src/providers/UserProvider';
import { APP_PROGRAM_ID } from 'src/contstant/commonLabels';

interface TranslationEditFormProps {
  question: Question;
  selectedLanguage: string;
}

const TranslationEditForm: React.FC<TranslationEditFormProps> = ({
  question,
  selectedLanguage,
}) => {
  const { user, userPreferences } = useUserDetails();
  const userId = user.userId;
  const programId = APP_PROGRAM_ID;
  const businessUnit = userPreferences.profileSettings.businessUnit;
  const navigate = useNavigate();
  const { clearQuestionAllApiStatus } = useQuestion();
  const { selectUserLanguage } = useUserProfile();
  const [showImageErrorModal, setShowImageErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<TranslationFormData | null>(
    null,
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const initialFormDataRef = useRef<TranslationFormData | null>(null);

  const { mutate: updateTranslation, isPending } = useUpdateQuestionTranslationMutation(
    { questionId: question.questionId, language: selectedLanguage },
    {
      onSuccess: () => {
        selectUserLanguage(selectedLanguage);
        openQuestionDetailsPage(navigate, question, true, false);
        clearQuestionAllApiStatus();
      },
      onError: (error: Error) => {
        // Close the confirmation modal
        setShowConfirmModal(false);
        setPendingFormData(null);

        // Log error structure for debugging
        console.error('Full error:', error);

        // Extract error message from API response
        let errorMessage = 'Failed to update translation';

        // Handle axios-style errors with response data
        const axiosError = error as Error & {
          response?: { data?: { message?: string } };
          data?: { message?: string };
        };

        if (axiosError?.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError?.data?.message) {
          errorMessage = axiosError.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        // Show error in Flashbar
        setValidationErrors([errorMessage]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
  );

  const content = getQuestionContent(question, selectedLanguage);
  const initialFormData = useTranslationFormData(question, selectedLanguage);

  const form = useForm({
    resolver: zodResolver(translationFormSchema),
    defaultValues: initialFormData,
    mode: 'onChange',
  });

  const { handleTextEditorChange, resetPreviousValues } = useImageChangeDetection({
    initialFormData,
    setValue: form.setValue,
    onImageChangeDetected: () => setShowImageErrorModal(true),
  });

  useEffect(() => {
    // Reset form with new initial data
    form.reset(initialFormData, { keepDefaultValues: false });
    // Update the ref after a microtask to ensure form has reset
    Promise.resolve().then(() => {
      initialFormDataRef.current = { ...initialFormData };
      resetPreviousValues(initialFormData);
    });
  }, [question.questionId, selectedLanguage]);

  if (initialFormDataRef.current === null) {
    initialFormDataRef.current = { ...initialFormData };
  }

  const handleSave = form.handleSubmit(
    (formData) => {
      // Clear any previous validation errors
      setValidationErrors([]);

      if (!initialFormDataRef.current) return;

      // Check if any images have changed before showing confirmation
      const fieldsToCheck = [
        'questionText',
        'auditInstruction',
        'passCriteria',
        'failCriteria',
        'justification',
        'taskDescription',
      ] as const;

      for (const field of fieldsToCheck) {
        if (hasImagesChanged(initialFormDataRef.current[field], formData[field])) {
          setShowImageErrorModal(true);
          return;
        }
      }

      // Check subquestions for image changes
      formData.subQuestions?.forEach((subQuestion, index) => {
        const initialSubQuestion = initialFormDataRef.current?.subQuestions?.[index];
        if (initialSubQuestion) {
          if (hasImagesChanged(initialSubQuestion.instruction, subQuestion.instruction)) {
            setShowImageErrorModal(true);
            return;
          }
          if (
            hasImagesChanged(
              initialSubQuestion.taskDetails?.taskDescription,
              subQuestion.taskDetails?.taskDescription,
            )
          ) {
            setShowImageErrorModal(true);
            return;
          }
        }
      });

      setPendingFormData(formData);
      setShowConfirmModal(true);
    },
    (errors) => {
      // Log validation errors for debugging

      // Collect error messages for display
      const errorMessages: string[] = [];
      const collectErrors = (obj: Record<string, unknown>, prefix = ''): void => {
        Object.entries(obj).forEach(([key, value]) => {
          const fieldPath = prefix ? `${prefix}.${key}` : key;
          if (value && typeof value === 'object') {
            const errorObj = value as { message?: string };
            if (errorObj.message) {
              errorMessages.push(`${fieldPath}: ${errorObj.message}`);
            } else {
              collectErrors(value as Record<string, unknown>, fieldPath);
            }
          }
        });
      };
      collectErrors(errors);

      if (errorMessages.length > 0) {
        setValidationErrors(errorMessages);
      }

      // Wait for React to render the error messages, then scroll to first error
      setTimeout(() => {
        const errorElement = document.querySelector('[class*="awsui_error"]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Scroll to top to show flashbar errors
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    },
  );

  const handleConfirmSave = () => {
    if (!pendingFormData || !initialFormDataRef.current) return;

    // Check if there are any dirty fields (more reliable than isDirty)
    const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;
    if (!hasDirtyFields) {
      setShowConfirmModal(false);
      setPendingFormData(null);
      navigate(-1);
      return;
    }

    const hasTaskContent =
      pendingFormData.taskName?.trim() && pendingFormData.taskDescription?.trim();

    // Build translation request - buildTranslationRequest handles URL replacement
    const translationRequest = buildTranslationRequest(
      question,
      {
        title: pendingFormData.title,
        questionText: pendingFormData.questionText,
        auditInstruction: pendingFormData.auditInstruction,
        passCriteria: pendingFormData.passCriteria,
        failCriteria: pendingFormData.failCriteria,
        justification: pendingFormData.justification,
      },
      pendingFormData.subQuestions,
      hasTaskContent && pendingFormData.taskName && pendingFormData.taskDescription
        ? {
            taskName: pendingFormData.taskName,
            taskDescription: pendingFormData.taskDescription,
          }
        : undefined,
      userId,
      programId,
      businessUnit,
      selectedLanguage,
    );

    updateTranslation(translationRequest);
    setShowConfirmModal(false);
    setPendingFormData(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingFormData(null);
  };

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const languageDisplayName = new Intl.DisplayNames(['en'], { type: 'language' }).of(
    selectedLanguage,
  );

  const showTaskDetails =
    content?.taskDetails &&
    (content.taskDetails.taskName?.trim() || content.taskDetails.taskDescription?.trim());

  return (
    <SpaceBetween size="l">
      {validationErrors.length > 0 && (
        <Flashbar
          items={[
            {
              type: 'error',
              dismissible: true,
              onDismiss: () => setValidationErrors([]),
              header: 'Validation Errors',
              content: (
                <SpaceBetween size="xs">
                  {validationErrors.map((error, index) => (
                    <div key={index}>• {error}</div>
                  ))}
                </SpaceBetween>
              ),
            },
          ]}
        />
      )}

      <Alert type="warning" header="Translation Mode">
        Only text content can be edited. Images, Categories, tags, locations, and audit
        scores are not editable.
      </Alert>

      <TranslationStalenessAlert
        question={question}
        selectedLanguage={selectedLanguage}
      />

      {!hasExistingTranslation(question, selectedLanguage) && (
        <Alert type="info" header="New Translation">
          No translation exists yet for {getLanguageLabel(selectedLanguage)} (
          {selectedLanguage}). The form below is pre-filled with the default language
          content as a reference. Please replace it with the translated text.
        </Alert>
      )}

      <QuestionReadonlyDetails question={question} selectedLanguage={selectedLanguage} />

      <TranslationQuestionContent
        control={form.control}
        errors={form.formState.errors}
        onTextEditorChange={handleTextEditorChange}
        resetField={form.resetField}
      />

      {showTaskDetails && (
        <TranslationTaskDetails
          control={form.control}
          errors={form.formState.errors}
          onTextEditorChange={handleTextEditorChange}
          resetField={form.resetField}
        />
      )}

      <TranslationSubQuestions
        control={form.control}
        errors={form.formState.errors}
        watch={form.watch}
        onTextEditorChange={handleTextEditorChange}
        resetField={form.resetField}
      />

      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={handleCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleSave()} loading={isPending}>
            Save
          </Button>
        </SpaceBetween>
      </Box>

      <ImageModificationErrorModal
        visible={showImageErrorModal}
        onDismiss={() => setShowImageErrorModal(false)}
      />

      <TranslationConfirmationModal
        visible={showConfirmModal}
        question={question}
        languageDisplayName={languageDisplayName || ''}
        initialFormData={initialFormDataRef.current || initialFormData}
        currentFormData={pendingFormData || form.getValues()}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelConfirm}
      />
    </SpaceBetween>
  );
};

export default TranslationEditForm;
